import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { MFA_STEP_UP_REQUIRED_MESSAGE } from '../constants/mfaStepUp';

const RETRY_HEADER = 'X-Mfa-Step-Up-Retry';

type Queued = {
  config: InternalAxiosRequestConfig;
  resolve: (value: AxiosResponse) => void;
  reject: (reason: unknown) => void;
};

const queue: Queued[] = [];

let openModal: (() => void) | null = null;

/** Called from `MfaStepUpModal` on mount so the axios interceptor can open the dialog. */
export function registerMfaStepUpModalOpener(fn: () => void): void {
  openModal = fn;
  if (queue.length > 0) {
    fn();
  }
}

export function getMfaStepUpQueueLength(): number {
  return queue.length;
}

/**
 * When a step-up–protected route returns 401 with `MFA step-up required`, enqueue the failed
 * request and return a Promise that resolves when MFA completes and the request is retried.
 */
function isStepUpRetryConfig(config: InternalAxiosRequestConfig): boolean {
  const h = config.headers;
  if (!h) return false;
  const fromGet = typeof (h as { get?: (k: string) => unknown }).get === 'function'
    ? (h as { get: (k: string) => unknown }).get(RETRY_HEADER)
    : undefined;
  const v = fromGet ?? (h as Record<string, string>)[RETRY_HEADER];
  return v === '1' || v === 1;
}

export function enqueueMfaStepUpFailure(error: AxiosError): Promise<AxiosResponse> {
  const config = error.config;
  if (!config) {
    return Promise.reject(error);
  }
  if (isStepUpRetryConfig(config)) {
    return Promise.reject(error);
  }

  return new Promise<AxiosResponse>((resolve, reject) => {
    queue.push({ config, resolve, reject });
    openModal?.();
  });
}

export function isMfaStepUpRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const ax = error as AxiosError<{ message?: string }>;
  return (
    ax.response?.status === 401 &&
    ax.response?.data?.message === MFA_STEP_UP_REQUIRED_MESSAGE
  );
}

/** After a new access token is stored, retry all queued requests (single wave). */
export async function flushMfaStepUpQueue(api: AxiosInstance): Promise<void> {
  const pending = queue.splice(0, queue.length);
  await Promise.all(
    pending.map(async item => {
      try {
        const res = await api.request({
          ...item.config,
          headers: {
            ...item.config.headers,
            [RETRY_HEADER]: '1',
          },
        });
        item.resolve(res);
      } catch (e) {
        item.reject(e);
      }
    })
  );
}

export function rejectMfaStepUpQueue(reason: unknown): void {
  while (queue.length) {
    const item = queue.shift()!;
    item.reject(reason);
  }
}
